import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  AreaChart, Area, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Cell,
} from "recharts";
import { api } from "./api";

// ── Utilitários ──────────────────────────────────────────

const fmt  = (n, d=2) => (n ?? 0).toFixed(d);
const fmtK = n => Math.abs(n) >= 1000
  ? `${(n/1000).toFixed(1)}k`
  : fmt(n);

function useData(fn, interval = 15000) {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try { setData(await fn()); } catch {}
  }, [fn]);
  useEffect(() => {
    load();
    const id = setInterval(load, interval);
    return () => clearInterval(id);
  }, [load, interval]);
  return data;
}

function useClock() {
  const [t, setT] = useState(new Date());
  useEffect(() => { const id = setInterval(() => setT(new Date()), 1000); return () => clearInterval(id); }, []);
  return t;
}

function getSessao(h) {
  if (h >= 0  && h < 8)  return { nome: "ÁSIA",    cor: "#f59e0b", ativa: true };
  if (h >= 8  && h < 13) return { nome: "LONDRES", cor: "#9f67ff", ativa: true };
  if (h >= 13 && h < 22) return { nome: "NEW YORK", cor: "#10b981", ativa: true };
  return { nome: "FECHADO", cor: "#4a5568", ativa: false };
}

// ── Componentes base ─────────────────────────────────────

function Badge({ children, color = "purple" }) {
  const map = {
    purple: "bg-purple-faint text-purple-light border-purple-dim",
    green:  "bg-green-dim text-green border-green/20",
    red:    "bg-red-dim text-red border-red/20",
    gold:   "bg-yellow-900/20 text-gold border-yellow-700/30",
    muted:  "bg-surface2 text-muted2 border-border",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[9px] font-mono font-bold border tracking-widest uppercase ${map[color]}`}>
      {children}
    </span>
  );
}

function Card({ children, className = "", glow = false }) {
  return (
    <div className={`
      bg-surface border border-border rounded-2xl relative overflow-hidden
      ${glow ? "animate-glow" : ""}
      ${className}
    `}>
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-purple/30 to-transparent" />
      {children}
    </div>
  );
}

function Dot({ color = "#9f67ff", pulse = false }) {
  return (
    <span className="relative flex h-2 w-2">
      {pulse && <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-40" style={{ background: color }} />}
      <span className="relative inline-flex rounded-full h-2 w-2" style={{ background: color }} />
    </span>
  );
}

// ── Métricas Hero ─────────────────────────────────────────

function HeroMetric({ pnl, wr, total, modo, sharpe }) {
  const pos = pnl >= 0;
  return (
    <Card glow className="p-5 mb-3">
      <div className="flex items-start justify-between mb-4">
        <div>
          <p className="text-[9px] text-muted tracking-[3px] uppercase mb-1">P&L Total</p>
          <p className={`font-display text-4xl font-extrabold tracking-tight ${pos ? "text-green" : "text-red"}`}>
            {pos ? "+" : "-"}${fmtK(Math.abs(pnl))}
          </p>
        </div>
        <Badge color={modo === "live" ? "green" : "gold"}>
          {modo || "paper"}
        </Badge>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: "Win Rate", value: `${fmt(wr * 100, 1)}%`, color: wr >= 0.5 ? "text-green" : "text-red" },
          { label: "Trades",   value: total,                   color: "text-purple-light" },
          { label: "Sharpe",   value: fmt(sharpe ?? 0),        color: "text-muted2" },
        ].map(m => (
          <div key={m.label} className="bg-surface2 rounded-xl p-3 border border-border">
            <p className="text-[8px] text-muted tracking-[2px] uppercase mb-1">{m.label}</p>
            <p className={`font-display font-bold text-lg ${m.color}`}>{m.value}</p>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ── Gráfico Equity ────────────────────────────────────────

function EquityChart({ trades }) {
  let acum = 0;
  const data = [...(trades || [])].reverse().map((t, i) => {
    acum += t.pnl_usd || 0;
    return { i: i + 1, v: +acum.toFixed(2) };
  });
  if (!data.length) return (
    <div className="h-32 flex items-center justify-center">
      <p className="text-muted text-xs">Rode o backtest para ver o gráfico</p>
    </div>
  );
  const pos = data[data.length - 1]?.v >= 0;
  const clr = pos ? "#10b981" : "#ef4444";
  return (
    <ResponsiveContainer width="100%" height={120}>
      <AreaChart data={data} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
        <defs>
          <linearGradient id="eq" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor={clr} stopOpacity={0.25} />
            <stop offset="100%" stopColor={clr} stopOpacity={0}    />
          </linearGradient>
        </defs>
        <CartesianGrid vertical={false} stroke="#1e2736" />
        <XAxis dataKey="i" hide />
        <YAxis hide domain={["auto","auto"]} />
        <Tooltip
          contentStyle={{ background:"#0d1017", border:"1px solid #1e2736", borderRadius:8, fontSize:11, fontFamily:"JetBrains Mono" }}
          formatter={v => [`$${v}`, "P&L"]}
          labelFormatter={(_, p) => (p && p[0] ? `Trade #${p[0].payload.i}` : "")}
        />
        <Area type="monotone" dataKey="v" stroke={clr} strokeWidth={2} fill="url(#eq)" dot={false} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

// ── P&L por Par ───────────────────────────────────────────

function PnlPorPar({ trades }) {
  const mapa = {};
  (trades || []).forEach(t => {
    const p = t.par || "?";
    mapa[p] = (mapa[p] || 0) + (t.pnl_usd || 0);
  });
  const data = Object.entries(mapa)
    .map(([par, pnl]) => ({ par, pnl: +pnl.toFixed(2) }))
    .sort((a, b) => b.pnl - a.pnl)
    .slice(0, 6);

  if (!data.length) return (
    <p className="text-muted text-xs text-center py-4">Sem dados ainda</p>
  );

  return (
    <ResponsiveContainer width="100%" height={100}>
      <BarChart data={data} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
        <XAxis dataKey="par" tick={{ fill:"#4a5568", fontSize:9, fontFamily:"JetBrains Mono" }} axisLine={false} tickLine={false} />
        <YAxis hide />
        <Tooltip
          contentStyle={{ background:"#0d1017", border:"1px solid #1e2736", borderRadius:8, fontSize:11, fontFamily:"JetBrains Mono" }}
          formatter={v => [`$${v}`, "P&L"]}
        />
        <Bar dataKey="pnl" radius={[4,4,0,0]}>
          {data.map((d, i) => (
            <Cell key={i} fill={d.pnl >= 0 ? "#10b981" : "#ef4444"} opacity={0.85} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

// ── Sessão Ativa ──────────────────────────────────────────

function SessaoCard({ clock }) {
  const h       = clock.getUTCHours();
  const sessao  = getSessao(h);
  const hStr    = clock.toUTCString().slice(17, 25);
  const proximas = [
    { nome: "ÁSIA",     inicio: 0,  fim: 8  },
    { nome: "LONDRES",  inicio: 8,  fim: 13 },
    { nome: "NEW YORK", inicio: 13, fim: 22 },
  ];
  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[9px] text-muted tracking-[3px] uppercase">Sessão Ativa</p>
        <p className="font-mono text-xs text-muted2">{hStr} UTC</p>
      </div>
      <div className="flex items-center gap-3 mb-4">
        <Dot color={sessao.cor} pulse={sessao.ativa} />
        <span className="font-display font-bold text-xl" style={{ color: sessao.cor }}>
          {sessao.nome}
        </span>
      </div>
      <div className="flex gap-2">
        {proximas.map(s => {
          const ativa = h >= s.inicio && h < s.fim;
          return (
            <div key={s.nome} className={`flex-1 rounded-lg p-2 border text-center transition-all ${
              ativa ? "border-purple-dim bg-purple-faint" : "border-border bg-surface2"
            }`}>
              <p className={`text-[8px] font-bold tracking-widest uppercase ${ativa ? "text-purple-light" : "text-muted"}`}>
                {s.nome.split(" ")[0]}
              </p>
              <p className={`text-[9px] mt-0.5 ${ativa ? "text-purple-light" : "text-muted"}`}>
                {s.inicio}h–{s.fim}h
              </p>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ── Motores IA ────────────────────────────────────────────

function MotoresIA({ regras }) {
  const motores = [
    { nome: "MOANAZ-LLM",   sub: "Llama 3.3 · Groq",          cor: "#9f67ff", ms: "~180ms" },
    { nome: "Risk Manager", sub: "SL/TP · Position sizing",    cor: "#10b981", ms: "local"  },
    { nome: "RAG Engine",   sub: "Memória · Regras do usuário", cor: "#f59e0b", ms: "~12ms"  },
  ];
  return (
    <Card className="p-4">
      <p className="text-[9px] text-muted tracking-[3px] uppercase mb-3">Motores de IA</p>
      <div className="space-y-2">
        {motores.map(m => (
          <div key={m.nome} className="flex items-center gap-3 p-3 rounded-xl bg-surface2 border border-border">
            <Dot color={m.cor} pulse />
            <div className="flex-1 min-w-0">
              <p className="font-mono font-semibold text-xs text-text truncate">{m.nome}</p>
              <p className="text-[9px] text-muted truncate mt-0.5">{m.sub}</p>
            </div>
            <div className="text-right">
              <Badge color="muted">{m.ms}</Badge>
            </div>
          </div>
        ))}
      </div>
      {regras?.length > 0 && (
        <div className="mt-3 p-3 rounded-xl bg-purple-faint border border-purple-dim">
          <p className="text-[9px] text-purple-light tracking-[2px] uppercase mb-2">
            Regras Ativas ({regras.length})
          </p>
          {regras.slice(0, 3).map((r, i) => (
            <p key={i} className="text-[10px] text-muted2 truncate mb-1">
              <span className="text-purple-light mr-1">›</span>
              {r.regra || r.valor}
            </p>
          ))}
          {regras.length > 3 && (
            <p className="text-[9px] text-muted mt-1">+{regras.length - 3} mais</p>
          )}
        </div>
      )}
    </Card>
  );
}

// ── Lista de Trades ───────────────────────────────────────

function TradeRow({ t }) {
  const pnl  = t.pnl_usd || 0;
  const pos  = pnl >= 0;
  const data = (t.data || "").slice(0, 16).replace("T", " ");
  return (
    <div className="flex items-center gap-3 py-3 border-b border-border/50 last:border-0">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-[10px] font-bold flex-shrink-0 border ${
        t.tipo === "long"
          ? "bg-purple-faint text-purple-light border-purple-dim"
          : "bg-red-dim text-red border-red/20"
      }`}>
        {t.tipo === "long" ? "BUY" : "SEL"}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-display font-bold text-sm text-text">{t.par}</span>
          {t.sessao && <Badge color="muted">{t.sessao}</Badge>}
        </div>
        <p className="text-[9px] text-muted mt-0.5 font-mono">{data}</p>
      </div>
      <div className="text-right flex-shrink-0">
        <p className={`font-mono font-bold text-sm ${pos ? "text-green" : "text-red"}`}>
          {pos ? "+" : ""}${fmt(pnl)}
        </p>
        <p className="text-[9px] text-muted mt-0.5">
          {((t.confianca || 0) * 100).toFixed(0)}% conf
        </p>
      </div>
    </div>
  );
}

// ── Aba Análise ───────────────────────────────────────────

function AbaAnalise({ trades }) {
  const porDia = {};
  (trades || []).forEach(t => {
    const d = new Date(t.data || Date.now());
    const dia = ["Dom","Seg","Ter","Qua","Qui","Sex","Sáb"][d.getDay()];
    if (!porDia[dia]) porDia[dia] = { wins: 0, total: 0, pnl: 0 };
    porDia[dia].total++;
    if ((t.pnl_usd || 0) > 0) porDia[dia].wins++;
    porDia[dia].pnl += t.pnl_usd || 0;
  });
  const diasData = ["Seg","Ter","Qua","Qui","Sex","Sáb","Dom"].map(d => ({
    dia: d,
    wr:  porDia[d] ? +(porDia[d].wins / porDia[d].total * 100).toFixed(0) : 0,
    pnl: porDia[d] ? +porDia[d].pnl.toFixed(2) : 0,
  }));

  return (
    <div className="space-y-3">
      <Card className="p-4">
        <p className="text-[9px] text-muted tracking-[3px] uppercase mb-3">Win Rate por Dia</p>
        <div className="flex items-end justify-between gap-1 h-16">
          {diasData.map(d => (
            <div key={d.dia} className="flex-1 flex flex-col items-center gap-1">
              <div className="w-full rounded-sm transition-all" style={{
                height: `${Math.max(d.wr, 4)}%`,
                background: d.wr >= 50
                  ? `rgba(16,185,129,${0.4 + d.wr/200})`
                  : d.wr > 0
                    ? `rgba(239,68,68,${0.4 + (100-d.wr)/200})`
                    : "#1e2736",
              }} />
              <p className="text-[8px] text-muted">{d.dia}</p>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-4">
        <p className="text-[9px] text-muted tracking-[3px] uppercase mb-3">P&L por Par</p>
        <PnlPorPar trades={trades} />
      </Card>
    </div>
  );
}

// ── Dashboard Principal ───────────────────────────────────

export function Dashboard({ user, onLogout }) {
  const clock   = useClock();
  const stats   = useData(api.stats);
  const trades  = useData(() => api.trades(100));
  const versoes = useData(api.versoes);
  const status  = useData(api.status);
  const regras  = useData(api.regras);

  const [tab, setTab] = useState("overview");

  const pnl   = stats?.pnl_total   || 0;
  const wr    = stats?.win_rate     || 0;
  const total = stats?.total_trades || 0;
  const modo  = status?.modo        || "paper";
  const sharpe = stats?.sharpe;

  const TABS = [
    { id: "overview", label: "Visão Geral", icon: "◈" },
    { id: "trades",   label: "Trades",      icon: "↗" },
    { id: "analise",  label: "Análise",     icon: "◎" },
    { id: "ia",       label: "IA",          icon: "⬡" },
  ];

  return (
    <div className="min-h-screen bg-bg">

      {/* Header */}
      <div className="sticky top-0 z-50 bg-bg/95 backdrop-blur-xl border-b border-border">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl bg-purple flex items-center justify-center shadow-lg" style={{ boxShadow: "0 0 16px #7c3aed55" }}>
              <span className="font-display font-black text-white text-sm">M</span>
            </div>
            <div>
              <p className="font-display font-black text-white text-base leading-none tracking-wide">MOANAZ</p>
              <p className="text-[8px] text-muted tracking-[3px] uppercase">Forex AI</p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap justify-end">
            <Link
              to="/backtest"
              className="flex items-center gap-2 px-3 py-1.5 border border-gray-700 rounded-lg
                         text-gray-300 hover:border-green-700 hover:text-green-400 transition-all text-xs font-mono"
            >
              🧪 Backtest Lab
            </Link>
            <Dot color="#10b981" pulse />
            <p className="font-mono text-[10px] text-muted2">
              {clock.toUTCString().slice(17, 22)} UTC
            </p>
            <button
              type="button"
              onClick={onLogout}
              className="text-[10px] text-muted hover:text-red transition-colors font-mono border border-border px-2 py-1 rounded-lg hover:border-red/40"
            >
              sair
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="max-w-lg mx-auto px-4 flex border-t border-border">
          {TABS.map(t => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`flex-1 py-2.5 flex flex-col items-center gap-0.5 transition-all relative ${
                tab === t.id ? "text-purple-light" : "text-muted hover:text-muted2"
              }`}
            >
              <span className="text-xs">{t.icon}</span>
              <span className="text-[8px] tracking-wider uppercase">{t.label}</span>
              {tab === t.id && (
                <span className="absolute bottom-0 left-1/4 right-1/4 h-px bg-purple-light rounded-full" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-lg mx-auto px-4 py-4 pb-8 space-y-3 animate-fade-in">

        {/* OVERVIEW */}
        {tab === "overview" && (
          <>
            <HeroMetric pnl={pnl} wr={wr} total={total} modo={modo} sharpe={sharpe} />

            {/* Gráfico equity */}
            <Card className="p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-[9px] text-muted tracking-[3px] uppercase">Curva de Equity</p>
                <Badge color={pnl >= 0 ? "green" : "red"}>
                  {pnl >= 0 ? "+" : ""}${fmt(pnl)}
                </Badge>
              </div>
              <EquityChart trades={trades || []} />
            </Card>

            <SessaoCard clock={clock} />

            {/* Últimos trades */}
            <Card className="p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-[9px] text-muted tracking-[3px] uppercase">Últimas Operações</p>
                <Badge color="muted">{(trades || []).length} total</Badge>
              </div>
              {!(trades || []).length
                ? <p className="text-muted text-xs text-center py-6">Nenhum trade ainda</p>
                : (trades || []).slice(0, 5).map((t, i) => <TradeRow key={i} t={t} />)
              }
            </Card>
          </>
        )}

        {/* TRADES */}
        {tab === "trades" && (
          <Card className="p-4">
            <div className="flex items-center justify-between mb-4">
              <p className="text-[9px] text-muted tracking-[3px] uppercase">Histórico Completo</p>
              <Badge color="muted">{(trades || []).length} trades</Badge>
            </div>
            {!(trades || []).length
              ? (
                <div className="text-center py-12">
                  <p className="text-muted text-sm">Nenhum trade registrado</p>
                  <p className="text-muted text-xs mt-1">Rode o backtest primeiro</p>
                </div>
              )
              : (trades || []).map((t, i) => <TradeRow key={i} t={t} />)
            }
          </Card>
        )}

        {/* ANÁLISE */}
        {tab === "analise" && <AbaAnalise trades={trades || []} />}

        {/* IA */}
        {tab === "ia" && (
          <>
            <MotoresIA regras={regras || []} />

            {/* Versões */}
            <Card className="p-4">
              <p className="text-[9px] text-muted tracking-[3px] uppercase mb-3">Evolução do Modelo</p>
              {!(versoes || []).length
                ? <p className="text-muted text-xs text-center py-4">Nenhuma versão ainda</p>
                : (versoes || []).map(v => (
                  <div key={v.versao} className="flex items-center gap-3 p-3 rounded-xl bg-surface2 border border-border mb-2">
                    <div className="w-9 h-9 rounded-xl bg-purple-faint border border-purple-dim flex items-center justify-center flex-shrink-0">
                      <span className="font-display font-black text-purple-light text-xs">v{v.versao}</span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs text-text">Modelo v{v.versao}</span>
                        <Badge color="purple">
                          WR {((v.win_rate || 0) * 100).toFixed(1)}%
                        </Badge>
                      </div>
                      <p className="text-[9px] text-muted mt-0.5">
                        {(v.total_steps || 0).toLocaleString()} steps · {(v.data || "").slice(0,10)}
                      </p>
                    </div>
                  </div>
                ))
              }
            </Card>

            {/* Info sistema */}
            <Card className="p-4">
              <p className="text-[9px] text-muted tracking-[3px] uppercase mb-3">Sistema</p>
              <div className="space-y-2">
                {[
                  { label: "Modo",      value: modo.toUpperCase(),        color: modo === "live" ? "text-green" : "text-gold" },
                  { label: "Pares",     value: status?.pares || "—",      color: "text-purple-light" },
                  { label: "Versão",    value: status?.versao || "2.0.0", color: "text-muted2" },
                  { label: "Usuário",   value: user?.email?.split("@")[0] || "—", color: "text-muted2" },
                ].map(item => (
                  <div key={item.label} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                    <span className="text-[9px] text-muted tracking-widest uppercase">{item.label}</span>
                    <span className={`font-mono text-xs font-semibold ${item.color}`}>{item.value}</span>
                  </div>
                ))}
              </div>
            </Card>

            <button
              type="button"
              onClick={onLogout}
              className="w-full py-3 rounded-xl border border-border text-muted text-xs font-mono hover:border-red/40 hover:text-red transition-all"
            >
              encerrar sessão
            </button>
          </>
        )}
      </div>
    </div>
  );
}
